import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User } from '../types'
import apiService from '../services/api'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (userData: RegisterData) => Promise<void>
  logout: () => void
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('auth_token')
    const userData = localStorage.getItem('user')
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData)
        setUser(parsedUser)
      } catch (error) {
        console.error('Error parsing user data:', error)
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
      }
    }
    setLoading(false)
  }, [])

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await apiService.postRaw<{ user: User; token: string }>('/auth/login', { email, password })
      
      if (response.data.success && response.data.data) {
        const { user: userData, token } = response.data.data
        
        localStorage.setItem('auth_token', token)
        localStorage.setItem('user', JSON.stringify(userData))
        
        setUser(userData)
      } else {
        throw new Error(response.data.error?.message || 'Login failed')
      }
    } catch (error: any) {
      let message = 'Login failed'
      
      if (error.response?.data?.error?.message) {
        message = error.response.data.error.message
      } else if (error.message) {
        message = error.message
      } else if (error.code === 'NETWORK_ERROR') {
        message = 'Cannot connect to server. Please check if the backend is running.'
      }
      
      throw new Error(message)
    }
  }

  const register = async (userData: RegisterData): Promise<void> => {
    try {
      const response = await apiService.postRaw<{ user: User; token: string }>('/auth/register', {
        email: userData.email,
        password: userData.password,
        first_name: userData.firstName,
        last_name: userData.lastName,
      })
      
      if (response.data.success && response.data.data) {
        const { user: newUser, token } = response.data.data
        
        localStorage.setItem('auth_token', token)
        localStorage.setItem('user', JSON.stringify(newUser))
        setUser(newUser)
      } else {
        throw new Error(response.data.error?.message || 'Registration failed')
      }
    } catch (error: any) {
      let message = 'Registration failed'
      
      if (error.response?.data?.error?.message) {
        message = error.response.data.error.message
        
        // Handle validation errors
        if (error.response.data.error.details) {
          const details = error.response.data.error.details
          const fieldErrors = Object.keys(details).map(field => 
            `${field}: ${details[field].join(', ')}`
          ).join('; ')
          message = `${message}: ${fieldErrors}`
        }
      } else if (error.message) {
        message = error.message
      }
      
      throw new Error(message)
    }
  }

  const logout = (): void => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    setUser(null)
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext